import { useLocation } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { renderMarkdown } from "@/lib/markdown";
import { useSettings } from "@/context/SettingsContext";

const CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    body: `We respect your privacy and are committed to protecting your personal data. This policy explains what we collect and how we use it.

## Information we collect
- Contact details you submit (name, email, WhatsApp number, country).
- Campaign details you provide (channel/video URLs, budget, goals).
- Basic analytics data (with your consent) such as pages visited and UTM source.

## How we use your information
- To respond to your consultation and inquiry requests.
- To provide and manage the services you request.
- To improve our website and communications.

## What we do NOT do
- We do not sell your personal data.
- We do not send spam.
- We do not expose customer information publicly.

## Cookies
We use essential cookies to operate the site and optional analytics cookies only after you consent. See our Cookie Policy for details.

## Data retention & security
We retain lead and client data only as long as necessary and protect it with reasonable security measures.

## Your rights
You may request access to, correction of, or deletion of your personal data by contacting us.

## Contact
For privacy questions, contact us using the details on our Contact page.`,
  },
  "terms": {
    title: "Terms of Service",
    body: `By using this website and our services, you agree to these terms.

## Our services
NewStarAds provides YouTube marketing and promotion services using compliant, policy-abiding methods.

## Results disclaimer
Results are not guaranteed. Advertising and promotion outcomes vary based on content, niche, budget and factors outside our control. We do not promise guaranteed views, subscribers, rankings, monetisation or viral results.

## Your responsibilities
- You are responsible for ensuring your content complies with YouTube's policies and applicable laws.
- You retain ownership of your channels and ad accounts.

## What we do not do
- We do not sell bots, fake engagement or artificial traffic.
- We do not guarantee monetisation approval or any specific metric.

## Payments
Campaign fees and terms are agreed per engagement. See our Refund Policy.

## Limitation of liability
To the maximum extent permitted by law, NewStarAds is not liable for indirect or consequential damages arising from use of our services.

## Changes
We may update these terms; continued use constitutes acceptance.`,
  },
  "refund-policy": {
    title: "Refund Policy",
    body: `We aim to be fair and transparent about payments and refunds.

## Consultations
Initial consultations are free and carry no obligation.

## Campaign fees
Because campaigns involve real ad spend and dedicated work, fees already spent on advertising or delivered services are generally non-refundable.

## Requesting a refund
If you believe a service was not delivered as agreed, contact us within 7 days and we will review your request in good faith.

## Ad spend
Ad spend paid to platforms (e.g. Google/YouTube) is controlled by those platforms and is subject to their policies.

## Contact
To discuss a refund, reach out via our Contact page.`,
  },
  "disclaimer": {
    title: "Disclaimer",
    body: `Please read this disclaimer carefully.

## No guaranteed results
NewStarAds does not guarantee any specific number of views, subscribers, watch time, rankings, monetisation or viral outcomes. All promotion results vary.

## Compliance
We only use methods intended to comply with YouTube's policies. You are responsible for ensuring your own content complies.

## No bots or fake engagement
We never sell or use bots, fake views, fake subscribers or artificial traffic.

## Independent platform
NewStarAds is an independent marketing agency and is not affiliated with, endorsed by, or sponsored by YouTube or Google.

## Informational content
Blog and website content is for general information and does not constitute professional, legal or financial advice.`,
  },
  "cookie-policy": {
    title: "Cookie Policy",
    body: `This policy explains how we use cookies and similar technologies.

## What are cookies?
Cookies are small text files stored on your device that help websites function and understand usage.

## Types we use
- **Essential cookies** — required for the site to work (always on).
- **Analytics cookies** — help us understand usage (loaded only with your consent).
- **Marketing cookies** — used for advertising measurement (loaded only with your consent).

## Managing consent
When you first visit, you can accept or reject non-essential cookies. We do not load optional tracking before you consent where required.

## Third parties
With consent, we may use Google Analytics and advertising pixels. These providers have their own privacy policies.

## Changes
We may update this policy from time to time.`,
  },
};

export default function Legal() {
  const { pathname } = useLocation();
  const page = pathname.replace(/^\//, "");
  const { settings } = useSettings();
  const brand = settings?.brand?.name || "NewStarAds";
  const data = CONTENT[page] || { title: "Not found", body: "This page does not exist." };

  return (
    <PublicLayout>
      <SEO title={`${data.title} | ${brand}`} description={`${data.title} for ${brand} — transparent, compliant YouTube marketing services.`} />
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <Reveal>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{data.title}</h1>
          <p className="mt-3 text-sm text-slate-400">Last updated: {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long" })}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8 prose-nsa" dangerouslySetInnerHTML={{ __html: renderMarkdown(data.body) }} />
      </section>
    </PublicLayout>
  );
}
